import { useState } from "react";
import { Home } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { ShieldPreview } from "./shield/ShieldPreview";
import { SIZES, sizeOf, shieldMm, eur } from "../lib/shield";

// Photo calibration: headboard (≈95 cm) spans 316 px of the 1264×848 image at the wall plane.
const IMG = { src: "/img/shield-wall-bedroom.jpg", w: 1264, h: 848, pxPerMm: 316 / 950, cx: 633, bottom: 314 };

const box = (size) => {
  const m = shieldMm(sizeOf(size));
  const w = m.w * IMG.pxPerMm, h = m.h * IMG.pxPerMm;
  return { left: `${((IMG.cx - w / 2) / IMG.w) * 100}%`, top: `${((IMG.bottom - h) / IMG.h) * 100}%`, width: `${(w / IMG.w) * 100}%` };
};

export const WallPreview = ({ design, initialSize }) => {
  const [size, setSize] = useState(initialSize || design?.size || "medium");
  const s = sizeOf(size);
  return (
    <div className="space-y-3" data-testid="wall-preview">
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Shield size">
        {SIZES.map((o) => (
          <button key={o.id} type="button" role="radio" aria-checked={size === o.id} onClick={() => setSize(o.id)} data-testid={`wall-size-${o.id}`}
            className={`border-2 px-3 py-1.5 text-xs font-extrabold uppercase transition-colors ${size === o.id ? "border-slate-900 bg-slate-900 text-lime-300" : "border-slate-200 bg-white hover:border-slate-900"}`}>
            {o.name} · {eur(o.price)}
          </button>
        ))}
      </div>
      <div className="relative overflow-hidden border-2 border-slate-900" style={{ aspectRatio: `${IMG.w} / ${IMG.h}` }}>
        <img src={IMG.src} alt="Child's bedroom with a single bed, used to show the shield to scale" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
        <div className="absolute transition-[left,top,width] duration-500 ease-out [filter:drop-shadow(0_6px_8px_rgba(0,0,0,.35))]" style={box(size)} data-testid="wall-preview-shield">
          <ShieldPreview design={{ ...design, guide: false }} testId="wall-shield-svg" />
        </div>
        <span className="absolute -translate-x-1/2 whitespace-nowrap bg-white/85 px-1.5 py-0.5 text-[10px] font-bold text-slate-700 sm:text-xs" style={{ left: `${(IMG.cx / IMG.w) * 100}%`, top: "40.5%" }}>Single bed · 90 cm</span>
      </div>
      <p className="text-sm text-slate-600" data-testid="wall-preview-caption">
        <strong>{s.name}</strong>: shield {Math.round(shieldMm(s).w / 10)} × {Math.round(s.h / 10)} cm, shown to scale above a 90 cm single bed.
      </p>
    </div>
  );
};

export const SeeOnWall = ({ design, size, testId = "see-on-wall-button" }) => (
  <Dialog>
    <DialogTrigger asChild>
      <button type="button" className="inline-flex items-center gap-1.5 text-sm font-bold underline decoration-lime-500 decoration-2 underline-offset-4 hover:text-lime-700" data-testid={testId}>
        <Home className="h-4 w-4" /> See it on a wall
      </button>
    </DialogTrigger>
    <DialogContent className="grid max-h-[90vh] max-w-4xl grid-cols-1 overflow-y-auto overflow-x-hidden rounded-none" data-testid="see-on-wall-dialog">
      <DialogHeader><DialogTitle className="font-display text-2xl font-black uppercase">See it on a wall</DialogTitle></DialogHeader>
      <WallPreview design={design} initialSize={size} />
    </DialogContent>
  </Dialog>
);
